import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CertificationTopicsTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function CertificationTopicsTab() {
  const [topics, setTopics] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', certificate_link: '', company_id: '' });
  const [exportingTopicId, setExportingTopicId] = useState(null);

  useEffect(() => {
    fetchTopics();
    fetchCompanies();
  }, []);

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/certification-topics`);
      setTopics(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке тем сертификаций: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/certification-companies`);
      setCompanies(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке компаний: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        company_id: parseInt(formData.company_id)
      };
      if (editingTopic) {
        await axios.put(`${API_URL}/api/certification-topics/${editingTopic.id}`, submitData);
      } else {
        await axios.post(`${API_URL}/api/certification-topics`, submitData);
      }
      setShowForm(false);
      setEditingTopic(null);
      setFormData({ title: '', description: '', certificate_link: '', company_id: '' });
      fetchTopics();
    } catch (err) {
      setError('Ошибка при сохранении темы: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (topic) => {
    setEditingTopic(topic);
    setFormData({
      title: topic.title,
      description: topic.description || '',
      certificate_link: topic.certificate_link || '',
      company_id: topic.company_id || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту тему? Все связанные вопросы также будут удалены.')) {
      try {
        await axios.delete(`${API_URL}/api/certification-topics/${id}`);
        fetchTopics();
      } catch (err) {
        setError('Ошибка при удалении темы: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTopic(null);
    setFormData({ title: '', description: '', certificate_link: '', company_id: '' });
  };

  const handleExportQuestions = async (topic) => {
    try {
      setExportingTopicId(topic.id);
      setError(null);
      const response = await axios.get(`${API_URL}/api/certification-questions`, {
        params: { topic_id: topic.id, limit: 100000 }
      });
      const questions = response.data || [];
      const jsonlContent = questions.map((q) => JSON.stringify(q)).join('\n');
      const blob = new Blob(['\uFEFF' + jsonlContent], { type: 'application/x-ndjson;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const safeTitle = (topic.title || `topic-${topic.id}`).replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 80);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certification-questions-${safeTitle}-${topic.id}.jsonl`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Ошибка при выгрузке вопросов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExportingTopicId(null);
    }
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="certification-topics-tab">
      <div className="tab-header">
        <h3>Управление темами сертификаций</h3>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить тему
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container">
          <h4>{editingTopic ? 'Редактировать тему' : 'Добавить новую тему'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Название темы *</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                maxLength={255}
                placeholder="Введите название темы"
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Описание</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Введите описание темы (необязательно)"
              />
            </div>
            <div className="form-group">
              <label htmlFor="certificate_link">Ссылка на сертификацию</label>
              <input
                type="url"
                id="certificate_link"
                value={formData.certificate_link}
                onChange={(e) => setFormData({ ...formData, certificate_link: e.target.value })}
                maxLength={500}
                placeholder="https://example.com/certificate (необязательно)"
              />
            </div>
            <div className="form-group">
              <label htmlFor="company_id">Компания *</label>
              <select
                id="company_id"
                value={formData.company_id}
                onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                required
              >
                <option value="">Выберите компанию</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingTopic ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Описание</th>
              <th>Компания</th>
              <th>Ссылка</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {topics.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  Нет тем. Добавьте первую тему сертификации.
                </td>
              </tr>
            ) : (
              topics.map((topic) => (
                <tr key={topic.id}>
                  <td>{topic.id}</td>
                  <td>{topic.title}</td>
                  <td className="description-cell">{topic.description || '—'}</td>
                  <td>{topic.company_name || '—'}</td>
                  <td>
                    {topic.certificate_link ? (
                      <a href={topic.certificate_link} target="_blank" rel="noopener noreferrer" className="link">
                        Открыть
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <button
                      className="btn-icon btn-export"
                      onClick={() => handleExportQuestions(topic)}
                      title="Скачать вопросы (JSONL)"
                      disabled={exportingTopicId === topic.id}
                    >
                      {exportingTopicId === topic.id ? '…' : '📥'}
                    </button>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(topic)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(topic.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CertificationTopicsTab;

